import { AgentChat } from '@cadstrom/cadstrom-shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useTRPC } from '#src/trpc.js';

import type { UIMessage } from 'ai';

/** Long-poll wait per run-state request; the server caps it independently. */
const RUN_STATE_WAIT_SECONDS = 25;
/** Pause between loop iterations while a local send is streaming. */
const LOCAL_RUN_PAUSE_MS = 1_000;
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** Cap on remembered handled-run ids so a long-lived watcher can't grow the set unbounded. */
const HANDLED_RUN_IDS_CAP = 100;
/** The slice of the AI SDK workbench helpers the watch loop drives. */
export interface ConversationRunWatchWorkbench {
	status: 'submitted' | 'streaming' | 'ready' | 'error';
	resumeStream: () => Promise<void>;
	setMessages: (messages: UIMessage[]) => void;
}

export interface ConversationRunWatchOptions {
	/** The conversation to watch; undefined for unsaved threads (watching idle). */
	conversationId: string | undefined;
	workbench: ConversationRunWatchWorkbench;
	enabled: boolean;
	/** Bearer token supplier for the run-state long-poll. */
	getAuthToken: () => Promise<string>;
	/**
	 * Force-fetches fresh checkpointed history for the given conversation,
	 * normalized for assistant-ui. Scoped by id so the watcher always loads the
	 * conversation it is watching, never whichever thread is currently active.
	 */
	loadHistory: (conversationId: string) => Promise<UIMessage[]>;
}

/** Indicates whether a checkpointed message belongs to an active ingest narration replay. */
function isActiveIngestNarrationMessage(message: UIMessage): boolean {
	if (message.id !== AgentChat.INGEST_NARRATION_OPENING_MESSAGE_ID) {
		return false;
	}
	return message.parts.some((part) => {
		if (part.type !== AgentChat.INGEST_NARRATION_STATE_PART_TYPE) {
			return false;
		}
		const state = AgentChat.ingestStateDataSchema.safeParse(part.data);
		return (
			state.success &&
			state.data.phase === AgentChat.IngestNarrationPhase.Narrating &&
			state.data.openingSettled === false
		);
	});
}

/**
 * Builds a unique resume base while preserving ordinary assistant turns.
 *
 * @param history - The conversation's checkpointed messages.
 * @param runId - The run being attached, used for a stable assistant stub ID.
 * @returns The messages to seed the workbench with before resuming.
 */
export function buildResumeBaseMessages(history: readonly UIMessage[], runId: string): UIMessage[] {
	const baseMessages = history.filter((message) => !isActiveIngestNarrationMessage(message));
	const last = baseMessages.at(-1);
	if (last?.role === 'assistant') {
		return [...baseMessages, { id: `watch-${runId}`, role: 'assistant', parts: [] }];
	}
	return baseMessages;
}

/** Resolves after `ms`, or immediately once the signal aborts. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		if (signal.aborted) {
			resolve();
			return;
		}
		let timer: ReturnType<typeof setTimeout>;
		const onAbort = (): void => {
			clearTimeout(timer);
			resolve();
		};
		// Drop the abort listener when the timer wins the race; otherwise every
		// pause/backoff iteration would leave a listener on the loop-lifetime
		// signal until unmount.
		timer = setTimeout(() => {
			signal.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal.addEventListener('abort', onAbort, { once: true });
	});
}

/**
 * Fetches the conversation's run state, parking server-side up to the wait
 * budget — the page's standing subscription to "did a run start?".
 *
 * @param args - Conversation, token supplier, and cancellation signal.
 * @returns The active runId, or null when none started within the wait.
 */
export async function fetchRunState(args: {
	conversationId: string;
	getAuthToken: () => Promise<string>;
	signal: AbortSignal;
}): Promise<string | null> {
	const token = await args.getAuthToken();
	const response = await fetch(
		`/api/v1/agent-chat/${args.conversationId}/run-state?waitSeconds=${RUN_STATE_WAIT_SECONDS}`,
		{
			headers: { Authorization: `Bearer ${token}` },
			signal: args.signal,
		},
	);
	if (!response.ok) {
		throw new Error(`Run-state request failed with status ${response.status}`);
	}
	const body = (await response.json()) as { activeRunId: string | null };
	return body.activeRunId;
}

/**
 * Watches a conversation for runs driven elsewhere — another browser session
 * or a background worker — and streams them into the local workbench. The loop:
 * long-poll run-state → on a new run, refetch history and resume the watch
 * stream (rendering tokens through the normal workbench pipeline) → on stream end,
 * refetch history to replace streamed state with the checkpointed truth →
 * long-poll again. Pauses while a local send is streaming (its own POST
 * response already renders live) and never re-attaches to a run it has
 * already handled.
 */
export function useConversationRunWatch(options: ConversationRunWatchOptions): void {
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	// The loop reads its collaborators through refs so re-renders (workbench helpers
	// are recreated per render) don't tear down the standing long-poll.
	const workbenchRef = useRef(options.workbench);
	const loadHistoryRef = useRef(options.loadHistory);
	const getAuthTokenRef = useRef(options.getAuthToken);
	useEffect(() => {
		workbenchRef.current = options.workbench;
		loadHistoryRef.current = options.loadHistory;
		getAuthTokenRef.current = options.getAuthToken;
	});

	const { conversationId, enabled } = options;

	// Synchronizing with an external system (the conversation's run stream) —
	// the justified use of an effect.
	useEffect(() => {
		if (!enabled || conversationId === undefined) {
			return;
		}
		const abort = new AbortController();
		const handledRunIds = new Set<string>();
		// Bounded remembering: a watcher left open across many runs evicts the
		// oldest handled id rather than growing the set forever. Ids are unique
		// and run-state only ever reports the current run, so an evicted (long
		// finished) id is never reported again to be re-attached.
		const markHandled = (runId: string): void => {
			handledRunIds.add(runId);
			if (handledRunIds.size > HANDLED_RUN_IDS_CAP) {
				const oldest = handledRunIds.values().next().value;
				if (oldest !== undefined) {
					handledRunIds.delete(oldest);
				}
			}
		};

		const isLocallyBusy = (): boolean =>
			workbenchRef.current.status === 'submitted' || workbenchRef.current.status === 'streaming';

		/**
		 * Attaches the local workbench to a remote run: loads history, resumes the
		 * watch stream, then replaces streamed state with the checkpointed truth.
		 *
		 * @returns True if the attach ran to completion; false if it exited early
		 *   because the watch was aborted or a local send took over — leaving the
		 *   still-active run eligible for a later retry rather than marking it
		 *   handled on a half-finished attach.
		 */
		const attach = async (runId: string): Promise<boolean> => {
			const history = await loadHistoryRef.current(conversationId);
			if (abort.signal.aborted || isLocallyBusy()) {
				return false;
			}
			workbenchRef.current.setMessages(buildResumeBaseMessages(history, runId));
			// GET the watch stream; an already-finished run answers 204 and this
			// resolves as a silent no-op.
			await workbenchRef.current.resumeStream();
			// A local send may have taken over while the stream resumed; bail
			// before overwriting local state or reporting the run handled, so it
			// is retried once the local send settles.
			if (abort.signal.aborted || isLocallyBusy()) {
				return false;
			}
			// Replace streamed state with the checkpointed truth after the replay.
			const finalHistory = await loadHistoryRef.current(conversationId);
			if (abort.signal.aborted || isLocallyBusy()) {
				return false;
			}
			workbenchRef.current.setMessages(finalHistory);
			// Conversation metadata (lastActivityAt, title) moved with the run.
			await queryClient.invalidateQueries({
				queryKey: trpc.agentChatConversation.get.queryKey({ conversationId }),
			});
			return true;
		};

		const loop = async (): Promise<void> => {
			let backoffMs = INITIAL_BACKOFF_MS;
			while (!abort.signal.aborted) {
				try {
					if (isLocallyBusy()) {
						await delay(LOCAL_RUN_PAUSE_MS, abort.signal);
						continue;
					}
					const runId = await fetchRunState({
						conversationId,
						getAuthToken: () => getAuthTokenRef.current(),
						signal: abort.signal,
					});
					backoffMs = INITIAL_BACKOFF_MS;
					if (abort.signal.aborted || runId === null || handledRunIds.has(runId) || isLocallyBusy()) {
						continue;
					}
					// Mark handled only after a clean attach: a transient failure
					// (history load, resume) throws into the catch and an early exit
					// (a local send took over) returns false — both leave the still-active
					// run eligible for a retry rather than blacklisting it for the loop's
					// lifetime.
					if (await attach(runId)) {
						markHandled(runId);
					}
				} catch {
					if (abort.signal.aborted) {
						return;
					}
					await delay(backoffMs, abort.signal);
					backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
				}
			}
		};
		void loop();
		return () => abort.abort();
	}, [enabled, conversationId, queryClient, trpc]);
}
